/* My Mori guest wishlist.
   Stores normalized product handles in localStorage.
*/

(function () {
  "use strict";

  const STORAGE_KEY = "mm_wishlist_handles_v1";
  const MAX_ITEMS = 100;
  const UPDATE_EVENT = "wishlist:updated";
  let wishlistPageRedraw = null;
  let renderVersion = 0;

  function normalizeList(value) {
    if (!Array.isArray(value)) return [];

    const unique = [];
    const seen = new Set();

    value.forEach((item) => {
      if (typeof item !== "string") return;
      const handle = item.trim().toLowerCase();
      if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle) || seen.has(handle)) return;
      seen.add(handle);
      unique.push(handle);
    });

    return unique.slice(0, MAX_ITEMS);
  }

  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return normalizeList(raw ? JSON.parse(raw) : []);
    } catch (error) {
      return [];
    }
  }

  function writeList(value) {
    const list = normalizeList(value);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      return false;
    }

    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { list } }));
    return true;
  }

  function has(handle) {
    return readList().includes(String(handle || "").toLowerCase());
  }

  function add(handle) {
    const normalizedHandle = normalizeList([handle])[0];
    if (!normalizedHandle) return false;

    const list = readList();
    if (list.includes(normalizedHandle)) return false;

    return writeList([...list, normalizedHandle]);
  }

  function remove(handle) {
    const normalizedHandle = normalizeList([handle])[0];
    if (!normalizedHandle) return false;

    const list = readList();
    if (!list.includes(normalizedHandle)) return false;

    return writeList(list.filter((item) => item !== normalizedHandle));
  }

  function toggle(handle) {
    return has(handle) ? remove(handle) : add(handle);
  }

  function clearAll() {
    if (!readList().length) return false;
    return writeList([]);
  }

  function setButtonState(button, inList) {
    const addLabel = button.dataset.wishlistLabelAdd || "Add to wishlist";
    const removeLabel = button.dataset.wishlistLabelRemove || "Remove from wishlist";
    const label = inList ? removeLabel : addLabel;

    button.setAttribute("aria-pressed", String(inList));
    button.setAttribute("aria-label", label);
    button.classList.toggle("is-active", inList);
    button.title = label;
  }

  function initButtons(root = document) {
    const buttons = root.querySelectorAll("[data-wishlist-button][data-product-handle]");
    const list = readList();

    buttons.forEach((button) => {
      const handle = button.dataset.productHandle;
      setButtonState(button, list.includes(handle));

      if (button.dataset.wishlistBound === "true") return;

      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const wasInList = has(handle);
        if (!toggle(handle)) return;

        if (!wasInList) flyHeartToHeader(button);
      });

      button.dataset.wishlistBound = "true";
    });
  }

  function updateHeaderWishlist() {
    const link = document.querySelector("[data-header-wishlist-link]");
    if (!link) return;

    const count = readList().length;
    link.classList.toggle("is-active", count > 0);
    link.dataset.wishlistCount = String(count);
    link.setAttribute("aria-label", count ? `Favorites, ${count} item${count === 1 ? "" : "s"}` : "Favorites");
  }

  function pulseHeader(target) {
    if (!target) return;
    target.classList.remove("is-wishlist-pulsing");
    window.requestAnimationFrame(() => target.classList.add("is-wishlist-pulsing"));
  }

  function flyHeartToHeader(fromButton) {
    const target = document.querySelector("[data-header-wishlist-link]");
    if (!target || !fromButton) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || typeof Element.prototype.animate !== "function") {
      pulseHeader(target);
      return;
    }

    const fromRect = fromButton.getBoundingClientRect();
    const toRect = target.getBoundingClientRect();
    const startX = fromRect.left + fromRect.width / 2;
    const startY = fromRect.top + fromRect.height / 2;
    const endX = toRect.left + toRect.width / 2;
    const endY = toRect.top + toRect.height / 2;
    const element = document.createElement("div");

    element.className = "mm-fly-heart";
    element.setAttribute("aria-hidden", "true");
    element.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M12 21s-7.2-4.5-9.6-8.4C.7 9.7 2 6.5 5 5.5c1.8-.6 3.8.1 5 1.6 1.2-1.5 3.2-2.2 5-1.6 3 1 4.3 4.2 2.6 7.1C19.2 16.5 12 21 12 21z" fill="currentColor"></path>
      </svg>
    `;

    document.body.appendChild(element);

    const animation = element.animate(
      [
        {
          transform: `translate(${startX}px, ${startY}px) translate(-50%, -50%) scale(1)`,
          opacity: 1,
        },
        {
          transform: `translate(${endX}px, ${endY}px) translate(-50%, -50%) scale(0.45)`,
          opacity: 0.85,
        },
      ],
      {
        duration: 680,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      }
    );

    animation.onfinish = () => {
      element.remove();
      pulseHeader(target);
    };

    animation.oncancel = () => element.remove();
  }

  async function fetchCard(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}?view=wishlist-card`, {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (response.status === 404) return { handle, status: "missing", html: "" };
    if (!response.ok) throw new Error(`Wishlist card request failed: ${response.status}`);

    return { handle, status: "ready", html: await response.text() };
  }

  function setPageStatus(element, message) {
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }

  function renderWishlistPage() {
    const grid = document.querySelector("[data-wishlist-grid]");
    if (!grid) {
      wishlistPageRedraw = null;
      return;
    }

    const emptyElement = document.querySelector("[data-wishlist-empty]");
    const clearButton = document.querySelector("[data-wishlist-clear]");
    const statusElement = document.querySelector("[data-wishlist-status]");

    if (clearButton && clearButton.dataset.wishlistBound !== "true") {
      clearButton.addEventListener("click", clearAll);
      clearButton.dataset.wishlistBound = "true";
    }

    wishlistPageRedraw = async function redraw() {
      const version = ++renderVersion;
      const list = readList();
      const isEmpty = list.length === 0;

      if (clearButton) clearButton.disabled = isEmpty;
      if (emptyElement) emptyElement.hidden = !isEmpty;
      setPageStatus(statusElement, "");

      if (isEmpty) {
        grid.replaceChildren();
        grid.removeAttribute("aria-busy");
        return;
      }

      grid.setAttribute("aria-busy", "true");
      grid.innerHTML = '<li class="wishlist-loading" role="status">Loading wishlist…</li>';

      const settled = await Promise.allSettled(list.map(fetchCard));
      if (version !== renderVersion) return;

      const cards = [];
      const missingHandles = [];
      let failedCount = 0;

      settled.forEach((result, index) => {
        if (result.status === "rejected") {
          failedCount += 1;
          return;
        }

        if (result.value.status === "missing") {
          missingHandles.push(list[index]);
          return;
        }

        cards.push(result.value);
      });

      if (missingHandles.length) {
        writeList(list.filter((handle) => !missingHandles.includes(handle)));
        return;
      }

      grid.innerHTML = cards
        .map(
          (card) => `
            <li class="wishlist-card-item" data-wishlist-card="${card.handle}">
              ${card.html}
            </li>
          `
        )
        .join("");

      grid.removeAttribute("aria-busy");
      initButtons(grid);

      if (failedCount) {
        setPageStatus(statusElement, "Some wishlist items could not be loaded. Please refresh the page.");
      }
    };

    wishlistPageRedraw();
  }

  function refreshWishlistUI() {
    initButtons(document);
    updateHeaderWishlist();
    if (wishlistPageRedraw) wishlistPageRedraw();
  }

  function initialize() {
    initButtons(document);
    updateHeaderWishlist();
    renderWishlistPage();
  }

  window.MM_WISHLIST = { readList, add, remove, toggle, clearAll, has };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener(UPDATE_EVENT, refreshWishlistUI);

  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) refreshWishlistUI();
  });

  document.addEventListener("shopify:section:load", (event) => {
    initButtons(event.target);
    updateHeaderWishlist();
    renderWishlistPage();
  });
})();
